/* Reusable pop-up behaviour: display triggers, frequency capping, iOS-safe scroll lock,
   and Zapier submission for the native-email capture mode. One script drives every
   .popup instance on the page. */
(function () {
  'use strict';

  var DESIGN_MODE = window.Shopify && window.Shopify.designMode;
  var lockedScrollY = 0;
  var openCount = 0;

  function lockScroll() {
    if (openCount++ > 0) return;
    lockedScrollY = window.pageYOffset || document.documentElement.scrollTop || 0;
    document.body.style.top = '-' + lockedScrollY + 'px';
    document.body.classList.add('popup-open');
  }
  function unlockScroll() {
    if (openCount > 0) openCount--;
    if (openCount > 0) return;
    document.body.classList.remove('popup-open');
    document.body.style.top = '';
    // Restore the scroll position instantly. The theme sets html { scroll-behavior: smooth },
    // which would otherwise animate this jump — making the page appear to scroll back down.
    var html = document.documentElement;
    var prevBehavior = html.style.scrollBehavior;
    html.style.scrollBehavior = 'auto';
    window.scrollTo(0, lockedScrollY);
    html.style.scrollBehavior = prevBehavior;
  }

  /* --- frequency gate (localStorage / sessionStorage) --- */
  function storageKey(id) { return 'onefarm_popup_' + id; }

  function alreadySeen(el) {
    var id = el.getAttribute('data-popup-id');
    var freq = el.getAttribute('data-frequency');
    try {
      if (freq === 'session') {
        return sessionStorage.getItem(storageKey(id)) === '1';
      }
      if (freq === 'days') {
        var raw = localStorage.getItem(storageKey(id));
        if (!raw) return false;
        var days = parseInt(el.getAttribute('data-frequency-days'), 10) || 7;
        return (Date.now() - parseInt(raw, 10)) < days * 86400000;
      }
      return false; // 'every' — never suppress
    } catch (e) {
      return false; // storage blocked (Safari private) → show
    }
  }

  function stampSeen(el) {
    var id = el.getAttribute('data-popup-id');
    var freq = el.getAttribute('data-frequency');
    try {
      if (freq === 'session') sessionStorage.setItem(storageKey(id), '1');
      else if (freq === 'days') localStorage.setItem(storageKey(id), String(Date.now()));
    } catch (e) { /* ignore */ }
  }

  /* --- "hide after sign-up" (persistent conversion flag) --- */
  var SUBSCRIBED_KEY = 'onefarm_subscribed';           // global: signed up via any pop-up
  function doneKey(id) { return 'onefarm_popup_done_' + id; } // per pop-up

  function hasSignedUp(el) {
    if (el.getAttribute('data-hide-after-signup') !== 'true') return false;
    try {
      if (el.getAttribute('data-signup-scope') === 'this') {
        return localStorage.getItem(doneKey(el.getAttribute('data-popup-id'))) === '1';
      }
      return !!localStorage.getItem(SUBSCRIBED_KEY); // 'any' — signed up anywhere on the site
    } catch (e) {
      return false;
    }
  }

  function markSignedUp(el) {
    try {
      localStorage.setItem(SUBSCRIBED_KEY, String(Date.now()));
      localStorage.setItem(doneKey(el.getAttribute('data-popup-id')), '1');
    } catch (e) { /* ignore */ }
  }

  /* --- open / close --- */
  function openPopup(el) {
    if (el.classList.contains('is-active')) return;
    el.classList.add('is-active');
    el.setAttribute('aria-hidden', 'false');
    lockScroll();
    stampSeen(el);
    track('popup_open', el);
    var focusable = el.querySelector('input, button, [href], select, textarea');
    if (focusable && !DESIGN_MODE) { try { focusable.focus({ preventScroll: true }); } catch (e) {} }
  }

  function closePopup(el) {
    if (!el.classList.contains('is-active')) return;
    el.classList.remove('is-active');
    el.setAttribute('aria-hidden', 'true');
    unlockScroll();
  }

  function track(event, el) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: event, popup_id: el.getAttribute('data-popup-id') });
  }

  /* --- triggers --- */
  function armTriggers(el) {
    var trigger = el.getAttribute('data-trigger');
    var fire = function () { openPopup(el); };

    if (trigger === 'load') {
      requestAnimationFrame(fire);
    } else if (trigger === 'delay') {
      var secs = parseFloat(el.getAttribute('data-delay')) || 5;
      setTimeout(fire, secs * 1000);
    } else if (trigger === 'scroll') {
      var pct = parseFloat(el.getAttribute('data-scroll')) || 40;
      var onScroll = function () {
        // True scroll depth: how far through the scrollable range the visitor is.
        // 0% at the top, 100% at the bottom — so it requires an actual scroll.
        var max = document.documentElement.scrollHeight - window.innerHeight;
        if (max <= 0) return; // page too short to scroll — wait rather than fire at the top
        var depth = window.pageYOffset / max * 100;
        if (depth >= pct) { window.removeEventListener('scroll', onScroll); fire(); }
      };
      window.addEventListener('scroll', onScroll, { passive: true });
      // NB: no immediate call — must wait for a real scroll event.
    } else if (trigger === 'exit') {
      var onLeave = function (e) {
        if (e.clientY <= 0) { document.removeEventListener('mouseout', onLeave); fire(); }
      };
      // exit-intent is desktop-only; on touch, fall back to a delayed show
      if (matchMedia('(hover: hover)').matches) {
        document.addEventListener('mouseout', onLeave);
      } else {
        setTimeout(fire, 15000);
      }
    }
  }

  /* --- Zapier submit (native email mode) --- */
  function wireForm(el) {
    var form = el.querySelector('[data-popup-form]');
    if (!form) return;
    var msg = form.querySelector('[data-popup-msg]');
    var webhook = form.getAttribute('data-webhook');

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      if (msg) { msg.hidden = true; msg.className = 'popup__msg'; }

      var emailInput = form.querySelector('input[type="email"]');
      var email = emailInput ? emailInput.value.trim() : '';
      if (!email) { showMsg(msg, form.getAttribute('data-error') || 'Please enter your email.', 'error'); return; }
      if (!webhook) { showMsg(msg, 'This form is not connected yet.', 'error'); return; }

      var btn = form.querySelector('button[type="submit"]');
      if (btn) { btn.disabled = true; }

      // URLSearchParams keeps this a "simple" request (no CORS preflight); Zapier parses it as fields.
      var body = new URLSearchParams();
      body.set('email', email);
      var nameInput = form.querySelector('input[name="first_name"]');
      if (nameInput && nameInput.value.trim()) body.set('first_name', nameInput.value.trim());
      body.set('source', form.getAttribute('data-source') || '');
      body.set('popup_id', el.getAttribute('data-popup-id'));
      body.set('page_url', window.location.href);

      fetch(webhook, { method: 'POST', mode: 'no-cors', body: body })
        .then(function () {
          track('popup_submit', el);
          markSignedUp(el);
          form.reset();
          showMsg(msg, form.getAttribute('data-success') || 'Thanks — you’re subscribed.', 'success');
          var fields = form.querySelector('[data-popup-fields]');
          if (fields) fields.style.display = 'none';
        })
        .catch(function () {
          if (btn) btn.disabled = false;
          showMsg(msg, 'Something went wrong. Please try again.', 'error');
        });
    });
  }

  function showMsg(msg, text, kind) {
    if (!msg) return;
    msg.textContent = text;
    msg.className = 'popup__msg is-' + kind;
    msg.hidden = false;
  }

  /* --- close wiring (delegated per popup) --- */
  function wireClose(el) {
    el.addEventListener('click', function (e) {
      if (e.target.closest('[data-popup-close]') || e.target === el) closePopup(el);
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && el.classList.contains('is-active')) closePopup(el);
    });
  }

  /* --- init --- */
  function initPopup(el) {
    if (el.dataset.popupInit) return;
    el.dataset.popupInit = '1';
    wireClose(el);
    wireForm(el);

    if (DESIGN_MODE) return;           // editor: open/close via section select events, no auto-trigger
    if (hasSignedUp(el)) return;       // already converted — don't show again
    if (alreadySeen(el)) return;       // frequency cap
    armTriggers(el);
  }

  function initAll() {
    document.querySelectorAll('.popup[data-popup-id]').forEach(initPopup);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  /* --- theme editor: preview on select --- */
  document.addEventListener('shopify:section:select', function (e) {
    var el = e.target.querySelector ? e.target.querySelector('.popup[data-popup-id]') : null;
    if (el) openPopup(el);
  });
  document.addEventListener('shopify:section:deselect', function (e) {
    var el = e.target.querySelector ? e.target.querySelector('.popup[data-popup-id]') : null;
    if (el) closePopup(el);
  });
  document.addEventListener('shopify:section:load', function (e) {
    var el = e.target.querySelector ? e.target.querySelector('.popup[data-popup-id]') : null;
    if (el) initPopup(el);
  });

  /* --- Hulk form conversions ---
     The HulkApps form iframe posts { type:'form_submitted', payload:{ form_id, email } }
     to its parent on a successful submit. We can't see inside the cross-origin iframe, but
     we can listen for that message and mark the matching pop-up as converted. */
  var HULK_ORIGIN = 'https://formbuilder.hulkapps.com';
  window.addEventListener('message', function (e) {
    if (e.origin !== HULK_ORIGIN) return;                 // only trust Hulk's own iframe
    var data = e.data;
    if (!data || data.type !== 'form_submitted') return;
    var formId = data.payload && data.payload.form_id;
    if (!formId) return;
    document.querySelectorAll('.popup[data-popup-id]').forEach(function (el) {
      if (el.getAttribute('data-hulk-form-id') === formId) {
        markSignedUp(el);
        track('popup_submit', el);
      }
    });
  });
})();
